import type { OrganizationSummary } from '@/features/organization-project-picker/types/organizationProjectPicker';
import type {
  OrganizationProjectBranch,
  OrganizationTreeBranchEdge,
  OrganizationTreeDensity,
  OrganizationTreeModel,
  OrganizationTreeRow,
} from '@/features/organization-project-picker/types/organizationTree';

interface VirtualTreeItem {
  index: number;
}

/** Stable estimates keep the tree responsive until React Virtual measures an active row. */
export function getOrganizationTreeRowEstimate(
  row: OrganizationTreeRow | undefined,
  density: OrganizationTreeDensity,
): number {
  const comfortable = density === 'comfortable';
  if (row?.kind === 'organization') return comfortable ? 52 : 29;
  if (row?.kind === 'project') return comfortable ? 58 : 30;
  if (row?.kind === 'project-footer') return 52;
  return comfortable ? 74 : 42;
}

type ProjectBranchRow = Omit<Extract<OrganizationTreeRow, { kind: 'project' }>, 'edge'>;
type ProjectStateRow = Omit<Extract<OrganizationTreeRow, { kind: 'project-state' }>, 'edge'>;
type ProjectFooterRow = Omit<Extract<OrganizationTreeRow, { kind: 'project-footer' }>, 'edge'>;
type ProjectTreeRow = ProjectBranchRow | ProjectStateRow | ProjectFooterRow;

function getBranchEdge(index: number, count: number): OrganizationTreeBranchEdge {
  if (count === 1) return 'only';
  if (index === 0) return 'first';
  if (index === count - 1) return 'last';
  return 'middle';
}

/** Builds the two-level, derived tree from the organization query and active project branches. */
export function getOrganizationTreeModel(
  organizations: readonly OrganizationSummary[],
  expandedOrganizationIds: ReadonlySet<string>,
  projectBranches: ReadonlyMap<string, OrganizationProjectBranch>,
): OrganizationTreeModel {
  return {
    nodes: organizations.map((organization) => {
      const branch = projectBranches.get(organization.organizationId);

      return {
        key: `organization:${organization.organizationId}`,
        organization: {
          ...organization,
          // The org-list count is authoritative (permission-aware) and stays
          // stable across expand/collapse. Use the expanded branch total only
          // while the list count is unavailable.
          projectCount: organization.projectCount ?? branch?.totalCount,
        },
        expanded: expandedOrganizationIds.has(organization.organizationId),
        branch,
      };
    }),
  };
}

/**
 * Flattens expanded branches from the derived tree into the single virtual canvas.
 *
 * `suppressOpenBranchTerminator` keeps the trailing row of a branch that still has
 * pages pending (but no footer yet) from rendering as the branch's end. The admin
 * table opts in because its `edge` drives a bottom border and a shortened nest
 * rail — a premature terminator there reads as "list ended." The Raw Footage
 * sidebar leaves it off: its `edge` only tunes trailing spacing, so suppressing it
 * would jitter the resting state without preventing any false terminator.
 */
export function getOrganizationTreeRows(
  tree: OrganizationTreeModel,
  { suppressOpenBranchTerminator = false }: { suppressOpenBranchTerminator?: boolean } = {},
): OrganizationTreeRow[] {
  return tree.nodes.flatMap((node) => {
    const { organization, branch } = node;
    const organizationRow: OrganizationTreeRow = {
      kind: 'organization',
      key: node.key,
      organization,
      expanded: node.expanded,
    };

    if (!node.expanded || !branch) return [organizationRow];

    const projectRows: ProjectTreeRow[] =
      branch.state === 'ready'
        ? branch.projects.map((project, index) => ({
            kind: 'project',
            key: `project:${organization.organizationId}:${project.projectId}`,
            organizationId: organization.organizationId,
            organization,
            project,
            projectIndex: index,
            projectCount: branch.projects.length,
          }))
        : [
            {
              kind: 'project-state',
              key: `project-state:${organization.organizationId}:${branch.state}`,
              organizationId: organization.organizationId,
              state: branch.state,
            },
          ];
    const branchRows: ProjectTreeRow[] = branch.showFooter
      ? [
          ...projectRows,
          {
            kind: 'project-footer',
            key: `project-footer:${organization.organizationId}`,
            organizationId: organization.organizationId,
          },
        ]
      : projectRows;

    // A branch only "closes" (its last row draws the terminator rail/border)
    // once every page has loaded. While more pages remain but no footer row is
    // present yet, keep the trailing project a non-terminator so it doesn't look
    // like the end of the list.
    const branchIsOpen = suppressOpenBranchTerminator && branch.hasNextPage && !branch.showFooter;
    return [
      organizationRow,
      ...branchRows.map((row, index) => ({
        ...row,
        edge:
          branchIsOpen && index === branchRows.length - 1
            ? branchRows.length === 1
              ? 'first'
              : 'middle'
            : getBranchEdge(index, branchRows.length),
      })),
    ];
  });
}

/**
 * Finds the lowest project branch that has reached its prefetch threshold in the
 * active range. Returns a single candidate per call — the bottom-most branch in
 * view. When two expanded branches' tails are co-visible, only the lower one
 * prefetches on a given tick; the upper one catches up on the next scroll tick.
 * This is intentional: pagination only fires during active scrolling, and normal
 * downward scrolling passes each branch's tail in turn.
 */
export function getVisibleProjectPaginationCandidate(
  rows: readonly OrganizationTreeRow[],
  virtualItems: readonly VirtualTreeItem[],
  threshold = 8,
): { organizationId: string; projectIndex: number; projectCount: number } | undefined {
  for (const virtualItem of [...virtualItems].reverse()) {
    const row = rows[virtualItem.index];
    if (row?.kind === 'project' && row.projectIndex >= row.projectCount - threshold) {
      return {
        organizationId: row.organizationId,
        projectIndex: row.projectIndex,
        projectCount: row.projectCount,
      };
    }
  }
  return undefined;
}
